"use client";

import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Lock,
  Ticket,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { toast } from "sonner";
import { saveEventAddonCart } from "@/actions/event-addons";
import { useAuthPrompt } from "@/components/auth";
import { BrandMark } from "@/components/branding/brand-mark";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer";
import Link from "@/components/ui/link";
import { useAuthState } from "@/hooks/use-auth-state";
import { useBackAwarePush } from "@/hooks/use-back-aware-push";
import { useNow } from "@/hooks/use-now";
import { brandScope, brandThemeStyle } from "@/lib/branding/brand-theme";
import { formatPriceCompact as formatPrice } from "@/lib/currency";
import { formatAbsolute } from "@/lib/dates/relative-time";
import type {
  AddonMenuItem,
  AddonSectionView,
  EventAddonMenu,
  SessionAddonState,
} from "@/lib/events/addon-types";
import { cn } from "@/lib/utils";
import {
  AddonDeadline,
  AddonQuantityControl,
  AddonThumb,
} from "./addon-parts.client";
import { FoodDietaryBadges } from "./food-dietary-badges";
import { eventMenuPath, MenuShareButton } from "./menu-share-button.client";

export type AddonSheetIntent = "browse" | "booked";

type Props = {
  menu: EventAddonMenu;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intent: AddonSheetIntent;
  initialOccurrenceId: number | null;
  /** Where "Book a ticket" goes when a section is for ticket holders only. */
  bookHref: string | null;
  /** Scroll to this section on open — a partner's shared menu link. */
  focusSectionId?: number | null;
};

function ExpandableText({
  text,
  lines,
  className,
}: {
  text: string;
  lines: 2 | 3;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setExpanded((current) => !current)}
      className={cn(
        "block w-full text-left whitespace-pre-line",
        !expanded && (lines === 2 ? "line-clamp-2" : "line-clamp-3"),
        className,
      )}
    >
      {text}
    </button>
  );
}

function ItemRow({
  item,
  quantity,
  canOrder,
  onChange,
  branded,
}: {
  item: AddonMenuItem;
  quantity: number;
  canOrder: boolean;
  onChange: (next: number) => void;
  branded: boolean;
}) {
  return (
    <li className="flex gap-4 py-4">
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "text-[15px] leading-snug font-semibold",
            branded
              ? "text-[var(--brand-ink)]"
              : "text-text-primary dark:text-white",
          )}
        >
          {item.name}
        </p>
        <FoodDietaryBadges dietary={item.foodDietary} />
        <p
          className={cn(
            "mt-1 text-sm font-semibold tabular-nums",
            branded
              ? "text-[var(--brand-accent-text)]"
              : "text-text-primary dark:text-white/90",
          )}
        >
          {formatPrice(item.priceMinor, item.currency)}
        </p>
        {item.description ? (
          <ExpandableText
            text={item.description}
            lines={2}
            className={cn(
              "mt-1.5 text-[13px] leading-snug",
              branded
                ? "text-[var(--brand-ink-muted)]"
                : "text-text-secondary dark:text-white/60",
            )}
          />
        ) : null}
      </div>
      {canOrder || quantity > 0 || item.image ? (
        <div
          className={cn(
            "relative w-[104px] shrink-0",
            item.image ? "pb-4" : "self-center",
          )}
        >
          {item.image ? (
            <AddonThumb
              image={item.image}
              alt={item.name}
              sizes="104px"
              className="size-[104px] rounded-2xl"
            />
          ) : null}
          {canOrder || quantity > 0 ? (
            <div className={cn(item.image && "absolute inset-x-2.5 bottom-0")}>
              <AddonQuantityControl
                name={item.name}
                quantity={quantity}
                disabled={!canOrder}
                onChange={onChange}
                branded={branded}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function SectionBlock({
  section,
  session,
  bookHref,
  now,
  quantities,
  setQuantity,
  titleInHeader = false,
  scoped = false,
}: {
  /** The sheet's own title already names this section; don't repeat it. */
  titleInHeader?: boolean;
  /** The surrounding surface is already this section's brand scope. */
  scoped?: boolean;
  section: AddonSectionView;
  session: SessionAddonState | null;
  bookHref: string | null;
  now: Date | null;
  quantities: Map<number, number>;
  setQuantity: (itemId: number, next: number) => void;
}) {
  const cutoffIso = session?.cutoffs[section.id] ?? null;
  const closed =
    cutoffIso !== null &&
    now !== null &&
    now.getTime() >= Date.parse(cutoffIso);
  const needsTicket = section.requiresTicket && !session?.bookingId;
  const canOrder = Boolean(session?.canOrder) && !closed && !needsTicket;
  // A branded section is its own panel in the partner's colours; its look
  // arrives resolved with the menu, so it paints correctly on first frame.
  const brand = section.brand;
  const branded = brand !== null;
  return (
    <section
      id={`addon-section-block-${section.id}`}
      className={cn(
        "scroll-mt-2",
        branded && !scoped ? "my-4 rounded-[26px] px-4 pt-4 pb-1" : "pt-5",
      )}
      style={brand && !scoped ? brandThemeStyle(brand) : undefined}
      aria-labelledby={`addon-section-${section.id}`}
    >
      {section.cover ? (
        <AddonThumb
          image={section.cover}
          alt=""
          sizes="(min-width: 672px) 640px, 100vw"
          className="mb-4 aspect-[16/7] w-full rounded-2xl"
        />
      ) : null}
      {/* In a scoped drawer the header already carries the mark. */}
      {brand && !scoped ? (
        <BrandMark
          brand={brand}
          name={section.partnerName || section.title}
          size="lg"
          className={titleInHeader ? undefined : "mb-2"}
        />
      ) : null}
      <h3
        id={`addon-section-${section.id}`}
        className={cn(
          "text-xl font-bold tracking-tight",
          branded
            ? "text-[var(--brand-ink)]"
            : "text-text-primary dark:text-white",
          titleInHeader && "sr-only",
        )}
      >
        {section.title}
      </h3>
      {/* The mark already says who it's by. */}
      {section.partnerName && !branded ? (
        <p className="mt-0.5 text-sm text-text-secondary dark:text-white/60">
          by{" "}
          <span className="font-semibold text-text-primary dark:text-white">
            {section.partnerName}
          </span>
        </p>
      ) : null}
      {section.description ? (
        <ExpandableText
          text={section.description}
          lines={3}
          className={cn(
            "mt-2.5 text-sm leading-relaxed",
            branded
              ? "text-[var(--brand-ink-muted)]"
              : "text-text-secondary dark:text-white/65",
          )}
        />
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {cutoffIso ? (
          <AddonDeadline cutoffIso={cutoffIso} now={now} branded={branded} />
        ) : null}
        {section.requiresTicket ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-black/5 px-2.5 py-1 text-xs font-medium text-text-secondary dark:bg-white/8 dark:text-white/65">
            <Ticket className="size-3.5" aria-hidden />
            Ticket holders only
          </span>
        ) : null}
      </div>
      {needsTicket ? (
        <div className="mt-3 flex items-center gap-3 rounded-2xl bg-black/[0.04] p-3 text-sm dark:bg-white/[0.06]">
          <Lock className="size-4 shrink-0 text-text-tertiary" aria-hidden />
          <span className="min-w-0 flex-1 text-text-secondary dark:text-white/65">
            Book a ticket for this date to pre-order these.
          </span>
          {bookHref ? (
            <Link
              href={bookHref}
              className="shrink-0 font-semibold text-brand dark:text-white"
            >
              Book
            </Link>
          ) : null}
        </div>
      ) : null}
      <ul
        className={cn(
          "mt-1 divide-y",
          branded
            ? "divide-[var(--brand-border)]"
            : "divide-black/6 dark:divide-white/8",
        )}
      >
        {section.items.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            quantity={quantities.get(item.id) ?? 0}
            canOrder={canOrder}
            onChange={(next) => setQuantity(item.id, next)}
            branded={branded}
          />
        ))}
      </ul>
    </section>
  );
}

/** What the viewer already has for this date — per counter, at a glance. */
function OrderedSummary({ session }: { session: SessionAddonState }) {
  const paid = session.orders.filter((order) =>
    ["paid", "fulfilled"].includes(order.status),
  );
  const pending = session.orders.filter(
    (order) => order.status === "pending_payment",
  );
  if (!paid.length && !pending.length) return null;
  const pickups = paid.flatMap((order) => order.pickups);
  const items = pickups.reduce(
    (sum, pickup) =>
      sum + pickup.lines.reduce((lineSum, line) => lineSum + line.quantity, 0),
    0,
  );
  return (
    <div className="mt-4 space-y-2">
      {paid.length ? (
        <Link
          href={`/wallet/preorders/${session.occurrenceId}`}
          className="block rounded-2xl border border-emerald-500/25 bg-emerald-500/8 p-3.5"
        >
          <span className="flex items-center gap-2">
            <CheckCircle2
              className="size-5 shrink-0 text-emerald-600 dark:text-emerald-400"
              aria-hidden
            />
            <span className="min-w-0 flex-1 text-sm font-semibold text-text-primary dark:text-white">
              You've pre-ordered {items} item{items === 1 ? "" : "s"}
            </span>
            <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
              Pickup passes
              <ChevronRight className="size-3.5" aria-hidden />
            </span>
          </span>
          <span className="mt-2 block space-y-1 pl-7">
            {pickups.map((pickup) => (
              <span
                key={pickup.id}
                className="block truncate text-xs text-text-secondary dark:text-white/60"
              >
                {pickup.collectedAtIso ? "✓ " : ""}
                {pickup.lines
                  .map((line) => `${line.quantity}× ${line.name}`)
                  .join(", ")}{" "}
                · at {pickup.counterLabel.replace(/^the /, "")}
              </span>
            ))}
          </span>
        </Link>
      ) : null}
      {pending.map((order) => (
        <Link
          key={order.id}
          href={`/shop/orders/${order.id}`}
          className="flex items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/8 p-3.5"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-text-primary dark:text-white">
              Payment not finished
            </span>
            <span className="block truncate text-xs text-text-secondary dark:text-white/60">
              {formatPrice(order.totalAmountMinor, order.currency)}
            </span>
          </span>
          <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
            Finish paying
          </span>
        </Link>
      ))}
    </div>
  );
}

/**
 * The add-on menu for one event date — the single place anything is ordered.
 *
 * No ticket needed: pre-orders belong to the person and the date, so a friend
 * whose ticket was bought for them orders their own food, and somebody can
 * order a drink later without touching the ticket. Quantities are local while
 * somebody taps through the menu; "Review & pay" commits the basket in one
 * write and hands over to the shared shop checkout.
 */
const PENDING_BASKET_KEY = "hiive:addon-pending-basket";
const PENDING_BASKET_TTL_MS = 30 * 60 * 1000;

function stashPendingBasket(
  occurrenceId: number,
  quantities: Map<number, number>,
) {
  try {
    window.sessionStorage.setItem(
      PENDING_BASKET_KEY,
      JSON.stringify({ occurrenceId, lines: [...quantities], at: Date.now() }),
    );
  } catch {
    // Private mode or blocked storage: they re-add after signing in.
  }
}

/** The stashed basket if it belongs to this menu, consumed on read. */
function takePendingBasket(menu: EventAddonMenu) {
  try {
    const raw = window.sessionStorage.getItem(PENDING_BASKET_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(PENDING_BASKET_KEY);
    const parsed = JSON.parse(raw) as {
      occurrenceId: number;
      lines: [number, number][];
      at: number;
    };
    const itemIds = new Set(
      menu.sections.flatMap((section) => section.items.map((item) => item.id)),
    );
    if (
      Date.now() - parsed.at > PENDING_BASKET_TTL_MS ||
      !menu.sessions.some((s) => s.occurrenceId === parsed.occurrenceId)
    )
      return null;
    const lines = parsed.lines.filter(
      ([itemId, quantity]) => itemIds.has(itemId) && quantity > 0,
    );
    return lines.length ? { occurrenceId: parsed.occurrenceId, lines } : null;
  } catch {
    return null;
  }
}

type AddonMenuState = ReturnType<typeof useAddonMenuState>;

/**
 * Everything the menu does, independent of where it is shown: the selected
 * date, the local basket (seeded from the server basket, or from a basket a
 * signed-out visitor built before signing in), the deadline clock, and
 * checkout. The drawer and the standalone menu page both run on this, so the
 * two can never disagree about a price or a rule.
 */
export function useAddonMenuState({
  menu,
  active,
  initialOccurrenceId,
  focusSectionId = null,
  resumeUrl,
}: {
  menu: EventAddonMenu;
  /** The drawer is open / the page is mounted. */
  active: boolean;
  initialOccurrenceId: number | null;
  focusSectionId?: number | null;
  /** Where sign-in returns to, to reopen this menu with the basket. */
  resumeUrl: () => string;
}) {
  const backAwarePush = useBackAwarePush();
  const { authStatus } = useAuthState();
  const { openAuthPrompt } = useAuthPrompt();
  const [occurrenceId, setOccurrenceId] = useState<number | null>(
    initialOccurrenceId ?? menu.sessions[0]?.occurrenceId ?? null,
  );
  const session =
    menu.sessions.find(
      (candidate) => candidate.occurrenceId === occurrenceId,
    ) ??
    menu.sessions[0] ??
    null;
  const [quantities, setQuantities] = useState(() => new Map<number, number>());
  const [pending, startTransition] = useTransition();

  useEffect(() => {
    if (active && initialOccurrenceId) setOccurrenceId(initialOccurrenceId);
  }, [active, initialOccurrenceId]);

  // A shared link names one section ("Food & drinks by Loudmouth"); land on
  // it once the menu has laid out. Nothing to do when it is the only one.
  useEffect(() => {
    if (!active || focusSectionId == null || menu.sections.length < 2) return;
    const timer = window.setTimeout(() => {
      document
        .getElementById(`addon-section-block-${focusSectionId}`)
        ?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [active, focusSectionId, menu.sections.length]);

  // Re-seed whenever the menu opens or the date changes: the basket on the
  // server is the truth, and another device may have changed it — unless a
  // signed-out visitor just came back from signing in with a basket built
  // here (a partner's shared link), which wins once.
  const pendingBasketRef = useRef<ReturnType<typeof takePendingBasket>>(null);
  const seededOccurrenceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!active) {
      seededOccurrenceRef.current = null;
      return;
    }
    // URL cleanup and server refreshes replace menu/session objects. Keep the
    // local draft until the menu closes or the buyer selects another date.
    if (seededOccurrenceRef.current === session?.occurrenceId) return;
    pendingBasketRef.current ??= takePendingBasket(menu);
    const pendingBasket = pendingBasketRef.current;
    if (pendingBasket) {
      // Switch to its date first; this effect runs again once it is active.
      if (pendingBasket.occurrenceId !== session?.occurrenceId) {
        setOccurrenceId(pendingBasket.occurrenceId);
        return;
      }
      seededOccurrenceRef.current = pendingBasket.occurrenceId;
      pendingBasketRef.current = null;
      setQuantities(new Map(pendingBasket.lines));
      return;
    }
    seededOccurrenceRef.current = session?.occurrenceId ?? null;
    setQuantities(
      new Map(
        (session?.cart.lines ?? []).map((line) => [line.itemId, line.quantity]),
      ),
    );
  }, [active, session, menu]);

  const now = useNow((current) => {
    const next = Object.values(session?.cutoffs ?? {})
      .map((cutoff) => Date.parse(cutoff) - current.getTime())
      .filter((delta) => delta > 0)
      .sort((a, b) => a - b)[0];
    if (next === undefined) return null;
    return next <= 6 * 3_600_000 ? 60_000 : 15 * 60_000;
  });

  const items = useMemo(
    () => new Map(menu.sections.flatMap((s) => s.items).map((i) => [i.id, i])),
    [menu.sections],
  );
  let count = 0;
  let totalMinor = 0;
  let currency = "INR";
  for (const [itemId, quantity] of quantities) {
    const item = items.get(itemId);
    if (!item || quantity <= 0) continue;
    count += quantity;
    totalMinor += item.priceMinor * quantity;
    currency = item.currency;
  }

  const setQuantity = (itemId: number, next: number) =>
    setQuantities((current) => {
      const updated = new Map(current);
      if (next <= 0) updated.delete(itemId);
      else updated.set(itemId, Math.min(next, 1000));
      return updated;
    });

  const checkout = () => {
    if (!session) return;
    if (authStatus !== "authenticated") {
      // Social sign-in leaves the page; keep the basket and reopen on return.
      stashPendingBasket(session.occurrenceId, quantities);
      openAuthPrompt({
        title: "Sign in to pre-order",
        description:
          "Your pre-order is saved to your account so you can show it at the counter.",
        callbackURL: resumeUrl(),
      });
      return;
    }
    startTransition(async () => {
      const result = await saveEventAddonCart({
        occurrenceId: session.occurrenceId,
        lines: [...quantities].map(([itemId, quantity]) => ({
          itemId,
          quantity,
        })),
      });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      if (result.data.cartId)
        backAwarePush(`/shop/cart?cartId=${result.data.cartId}`);
    });
  };

  const single = menu.sections.length === 1 ? menu.sections[0] : null;
  return {
    session,
    setOccurrenceId,
    quantities,
    setQuantity,
    now,
    count,
    totalMinor,
    currency,
    pending,
    checkout,
    single,
    /** One branded menu owns the whole surface; a mixed one doesn't. */
    menuBrand: single?.brand ?? null,
    hasOrders: Boolean(
      session?.orders.some((order) =>
        ["paid", "fulfilled"].includes(order.status),
      ),
    ),
  };
}

/** Date chips, what's already ordered, and every section. */
export function AddonMenuBody({
  menu,
  state,
  bookHref,
  titleInHeader,
}: {
  menu: EventAddonMenu;
  state: AddonMenuState;
  bookHref: string | null;
  /** The container's own title already names the single section. */
  titleInHeader: boolean;
}) {
  const { session } = state;
  return (
    <>
      {menu.sessions.length > 1 ? (
        <div className="flex shrink-0 gap-2 overflow-x-auto pt-1 pb-2">
          {menu.sessions.map((candidate) => (
            <button
              key={candidate.occurrenceId}
              type="button"
              onClick={() => state.setOccurrenceId(candidate.occurrenceId)}
              className={cn(
                "shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                candidate.occurrenceId === session?.occurrenceId
                  ? "border-brand bg-brand text-brand-foreground"
                  : "border-black/10 text-text-secondary dark:border-white/15 dark:text-white/70",
              )}
            >
              {formatAbsolute(candidate.startIso, {
                weekday: "short",
                day: "numeric",
                month: "short",
              })}
              {candidate.bookingId ? " · your ticket" : ""}
            </button>
          ))}
        </div>
      ) : null}
      {session ? <OrderedSummary session={session} /> : null}
      <div className="divide-y divide-black/8 dark:divide-white/10">
        {menu.sections.map((section) => (
          <SectionBlock
            key={section.id}
            section={section}
            session={session}
            bookHref={bookHref}
            now={state.now}
            quantities={state.quantities}
            setQuantity={state.setQuantity}
            titleInHeader={section === state.single && titleInHeader}
            // The whole surface is already this brand: no inner panel.
            scoped={state.menuBrand !== null}
          />
        ))}
      </div>
    </>
  );
}

/** The basket total and "Review & pay", or why nothing can be ordered. */
export function AddonPayBar({ state }: { state: AddonMenuState }) {
  const { count, pending, menuBrand } = state;
  return count > 0 ? (
    <Button
      type="button"
      disabled={pending}
      onClick={state.checkout}
      className={cn(
        "h-12 w-full justify-between rounded-2xl px-4 text-base",
        menuBrand ? "hover:opacity-90" : "dark:bg-brand",
      )}
      // One branded menu: pay in its colour. A mixed basket keeps the app's
      // own, since no single partner owns it.
      style={
        menuBrand
          ? { backgroundColor: menuBrand.accent, color: menuBrand.accentInk }
          : undefined
      }
    >
      <span className="tabular-nums">
        {count} item{count === 1 ? "" : "s"} ·{" "}
        {formatPrice(state.totalMinor, state.currency)}
      </span>
      <span className="inline-flex items-center gap-1.5 font-semibold">
        {pending ? "Saving…" : "Review & pay"}
        <ArrowRight className="size-4" aria-hidden />
      </span>
    </Button>
  ) : (
    <p className="py-2 text-center text-sm text-text-secondary dark:text-white/60">
      {state.session?.canOrder
        ? "Add what you'd like — you'll review before paying."
        : "Pre-orders for this date have closed."}
    </p>
  );
}

export function EventAddonsSheet({
  menu,
  open,
  onOpenChange,
  intent,
  initialOccurrenceId,
  bookHref,
  focusSectionId = null,
}: Props) {
  const state = useAddonMenuState({
    menu,
    active: open,
    initialOccurrenceId,
    focusSectionId,
    resumeUrl: () => `${window.location.pathname}?menu=resume`,
  });
  const { single, hasOrders, menuBrand } = state;
  // A single branded menu takes over the whole drawer — header, body and pay
  // bar — rather than sitting as a panel inside the app's grey sheet.
  const scope = menuBrand ? brandScope(menuBrand) : null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        overlayClassName="z-[1190]"
        className={cn(
          "z-[1200] mx-auto max-h-[92dvh] max-w-2xl rounded-t-[30px] shadow-xl",
          scope
            ? cn(scope.className, "border-[var(--brand-border)]")
            : "border-border bg-surface text-text-primary dark:border-white/[0.14] dark:bg-[#111111] dark:text-white",
        )}
        style={scope?.style}
        data-brand-scope={scope?.["data-brand-scope"]}
      >
        <div className="flex shrink-0 items-start gap-3 px-5 pt-3 pb-3">
          <div className="min-w-0 flex-1">
            {menuBrand && single ? (
              <BrandMark
                brand={menuBrand}
                name={single.partnerName || single.title}
                size="md"
                className="mb-2"
              />
            ) : null}
            <p className="truncate text-[11px] font-semibold tracking-[0.14em] text-text-tertiary uppercase">
              {menu.event.name}
            </p>
            <DrawerTitle className="mt-1 text-2xl font-bold tracking-tight text-text-primary">
              {intent === "booked"
                ? "You're in. Add to your visit?"
                : hasOrders
                  ? "Order more"
                  : single
                    ? single.title
                    : "Add to your visit"}
            </DrawerTitle>
            <DrawerDescription className="mt-1 text-sm text-text-secondary">
              {intent === "booked"
                ? "Optional — pre-order now and it's ready when you arrive."
                : "Optional extras, paid separately and collected at the venue."}
            </DrawerDescription>
          </div>
          <MenuShareButton
            path={eventMenuPath(menu.event.hiiveSlug, menu.event.slug)}
            title={single?.title ?? `${menu.event.name} menu`}
            text={`Pre-order for ${menu.event.name}.`}
            className="size-9 shrink-0 justify-center rounded-full text-text-secondary hover:bg-black/5 dark:hover:bg-white/10"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6">
          <AddonMenuBody
            menu={menu}
            state={state}
            bookHref={bookHref}
            titleInHeader={intent !== "booked" && !hasOrders}
          />
        </div>

        <div
          className={cn(
            "border-t px-5 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.85rem)] backdrop-blur-xl",
            scope
              ? "border-[var(--brand-border)] bg-[var(--brand-surface)]"
              : "border-black/8 bg-surface/95 dark:border-white/10 dark:bg-[#111111]/95",
          )}
        >
          <AddonPayBar state={state} />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
